import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, KeyRound, Users, Plus, Ban, UserMinus, ShieldCheck, Cloud, Send, Wallet, CheckCircle2, XCircle, MinusCircle, Zap, AlertTriangle, Trash2 } from "lucide-react";
import { apiPost } from "@/lib/api";
import { Spinner } from "@/components/Spinner";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function AdminPanel({ onClose }) {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [engineEnabled, setEngineEnabled] = useState(true);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    try {
      const d = await apiPost("/api/auth/admin/engine-diagnostics", { admin_pin: pin });
      setDiag(d || null);
    } catch { setDiag(null); }
    finally { setDiagLoading(false); }
  }, [pin]);
  const [section, setSection] = useState("codes");
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState("1");

  const loadData = useCallback(async (p) => {
    const usePin = p || pin;
    const [c, u] = await Promise.all([
      apiPost("/api/auth/admin/codes/list", { admin_pin: usePin }),
      apiPost("/api/auth/admin/users/list", { admin_pin: usePin }),
    ]);
    setCodes((c && c.codes) || []);
    setUsers((u && u.users) || []);
    setEngineEnabled(u ? u.engine_enabled !== false : true);
  }, [pin]);

  const verify = async () => {
    setLoading(true); setError("");
    try {
      const data = await apiPost("/api/auth/admin/verify", { admin_pin: pin });
      if (data && data.authorized) { setAuthed(true); loadData(pin); }
      else setError("Invalid admin PIN");
    } catch { setError("Connection error"); }
    finally { setLoading(false); }
  };

  const createCode = async () => {
    setLoading(true);
    try {
      const data = await apiPost("/api/auth/admin/codes/create", { admin_pin: pin, label: label || undefined, max_uses: parseInt(maxUses) || 1 });
      if (data && data.success) { setLabel(""); setMaxUses("1"); loadData(); toast.success(`New code created: ${data.code}`); }
      else toast.error("Failed to create code");
    } catch { toast.error("Failed to create code"); }
    finally { setLoading(false); }
  };

  const revokeCode = async (code) => { await apiPost("/api/auth/admin/codes/revoke", { admin_pin: pin, code }); loadData(); };
  const revokeUser = async (token) => { await apiPost("/api/auth/admin/users/revoke", { admin_pin: pin, token }); loadData(); };

  // Destructive (hard) deletes — confirmed via dialog
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, run }
  const [confirmLoading, setConfirmLoading] = useState(false);

  const runConfirm = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try { await confirmAction.run(); }
    finally { setConfirmLoading(false); setConfirmAction(null); }
  };

  const askDeleteCode = (code) => setConfirmAction({
    title: "Delete Invite Code",
    message: `Permanently delete code ${code}? This cannot be undone. Existing users who already signed in keep their access.`,
    run: async () => {
      const r = await apiPost("/api/auth/admin/codes/delete", { admin_pin: pin, code });
      if (r && r.success) { toast.success(`Code ${code} deleted`); loadData(); }
      else toast.error((r && r.detail) || "Failed to delete code");
    },
  });

  const askDeleteUser = (token, name) => setConfirmAction({
    title: "Delete User & Profile",
    message: `Permanently delete ${name || "this user"} and their profile? This removes their account data and all sessions. Use this to clear dummy/test profiles.`,
    run: async () => {
      const r = await apiPost("/api/auth/admin/users/delete", { admin_pin: pin, token });
      if (r && r.success) { toast.success("User & profile deleted"); loadData(); loadDiag(); }
      else toast.error((r && r.detail) || "Failed to delete user");
    },
  });

  if (!authed) {
    return (
      <div className="min-h-screen">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1e293b]">
          <button onClick={onClose} className="text-[#f1f5f9]"><ArrowLeft size={24} /></button>
          <span className="text-lg font-bold text-[#f1f5f9]">Admin Panel</span>
          <span className="w-6" />
        </div>
        <div className="flex flex-col items-center justify-center px-8 pt-24 max-w-sm mx-auto">
          <ShieldCheck size={48} color="#f59e0b" />
          <p className="text-lg text-[#f1f5f9] font-semibold mt-4 mb-6">Enter Admin PIN</p>
          <input type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()}
            placeholder="PIN" data-testid="admin-panel-pin"
            className="w-full bg-[#1e293b] border border-[#334155] rounded-xl p-4 text-2xl text-[#f1f5f9] text-center tracking-[8px] outline-none focus:border-[#f59e0b]" autoFocus />
          {error && <p className="text-[#ef4444] text-[13px] mt-2">{error}</p>}
          <button onClick={verify} disabled={loading} data-testid="admin-panel-unlock" className="w-full bg-[#f59e0b] py-4 rounded-xl text-white font-semibold mt-4 disabled:opacity-60">{loading ? <Spinner size={20} color="#fff" /> : "Unlock"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1e293b]">
        <button onClick={onClose} className="text-[#f1f5f9]"><ArrowLeft size={24} /></button>
        <span className="text-lg font-bold text-[#f1f5f9]">Admin Panel</span>
        <button onClick={() => loadData()} className="text-[#10b981]"><RefreshCw size={22} /></button>
      </div>
      <div className="flex border-b border-[#1e293b]">
        {[{ k: "codes", l: `Codes (${codes.length})`, I: KeyRound }, { k: "users", l: `Users (${users.filter((u) => u.active).length})`, I: Users }, { k: "engine", l: "Engine", I: Zap }].map(({ k, l, I }) => (
          <button key={k} onClick={() => { setSection(k); if (k === "engine") loadDiag(); }} data-testid={`admin-section-${k}`} className={`flex-1 flex items-center justify-center gap-1.5 py-3 ${section === k ? "border-b-2 border-[#10b981] text-[#10b981] font-semibold" : "text-[#64748b]"}`}><I size={18} /> {l}</button>
        ))}
      </div>

      <div className="p-4 max-w-6xl mx-auto">
        {section === "codes" && (
          <>
            <p className="text-sm text-[#94a3b8] font-semibold mb-2">Create Invite Code</p>
            <div className="flex gap-2 mb-5 max-w-xl">
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" data-testid="new-code-label" className="flex-[2] bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2.5 text-[#f1f5f9] outline-none" />
              <input value={maxUses} onChange={(e) => setMaxUses(e.target.value)} inputMode="numeric" placeholder="Uses" data-testid="new-code-uses" className="flex-1 bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2.5 text-[#f1f5f9] outline-none" />
              <button onClick={createCode} disabled={loading} data-testid="create-code-btn" className="w-11 bg-[#10b981] rounded-lg flex items-center justify-center text-white"><Plus size={20} /></button>
            </div>
            {codes.length === 0 ? <p className="text-[#64748b] text-center mt-8">No invite codes yet. Create one above.</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {codes.map((c) => (
              <div key={c.code} className={`bg-[#1e293b] border border-[#334155] rounded-xl p-3.5 mb-2.5 ${!c.active ? "opacity-50" : ""}`}>
                <div className="flex justify-between items-center mb-1.5"><span className="text-lg font-bold text-[#f1f5f9] tracking-[2px]">{c.code}</span><span className="px-2 py-0.5 rounded text-[11px] font-semibold text-[#f1f5f9]" style={{ backgroundColor: c.active ? "#10b98130" : "#ef444430" }}>{c.active ? "Active" : "Revoked"}</span></div>
                <p className="text-[13px] text-[#94a3b8] mb-1.5">{c.label}</p>
                <div className="flex flex-wrap gap-3 mb-2"><span className="text-[12px] text-[#64748b]">Uses: {c.uses}/{c.max_uses > 0 ? c.max_uses : "∞"}</span>{c.last_used_at && <span className="text-[12px] text-[#64748b]">Last used: {new Date(c.last_used_at).toLocaleDateString()}</span>}</div>
                <div className="flex items-center gap-2 justify-end">
                  {c.active && <button onClick={() => revokeCode(c.code)} data-testid={`revoke-code-${c.code}`} className="flex items-center gap-1 bg-[#f59e0b]/20 text-[#f59e0b] text-[12px] font-semibold px-2.5 py-1.5 rounded-md"><Ban size={14} /> Revoke</button>}
                  <button onClick={() => askDeleteCode(c.code)} data-testid={`delete-code-${c.code}`} className="flex items-center gap-1 bg-[#ef4444]/20 text-[#ef4444] text-[12px] font-semibold px-2.5 py-1.5 rounded-md"><Trash2 size={14} /> Delete</button>
                </div>
              </div>
            ))}
              </div>
            )}
          </>
        )}
        {section === "users" && (
          users.length === 0 ? <p className="text-[#64748b] text-center mt-8">No users have used an invite code yet.</p> : (
            <>
            {!engineEnabled && (
              <div data-testid="engine-off-banner" className="flex items-start gap-2 bg-[#ef4444]/15 border border-[#ef4444]/40 rounded-xl p-3 mb-3 text-[#fca5a5]">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span className="text-[13px] font-semibold">Trading engine is OFF on this server — no user will auto-trade until it's enabled and redeployed.</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {users.map((u) => {
              const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
              const Stat = ({ ok, label }) => (
                <span className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: ok === true ? "#10b981" : ok === false ? "#ef4444" : "#64748b" }}>
                  {ok === true ? <CheckCircle2 size={13} /> : ok === false ? <XCircle size={13} /> : <MinusCircle size={13} />} {label}
                </span>
              );
              const Row = ({ k, v }) => (v ? <div className="flex justify-between gap-3 text-[12px]"><span className="text-[#64748b]">{k}</span><span className="text-[#cbd5e1] truncate max-w-[60%] text-right">{v}</span></div> : null);
              return (
              <div key={u.token || u.token_short} data-testid={`admin-user-card-${u.token_short}`} className={`bg-[#1e293b] border border-[#334155] rounded-xl p-3.5 ${!u.active ? "opacity-50" : ""}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-bold text-[#f1f5f9]">{fullName || u.token_short}</span>
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold text-[#f1f5f9]" style={{ backgroundColor: u.active ? "#10b98130" : "#ef444430" }}>{u.active ? "Active" : "Revoked"}</span>
                </div>

                {/* Wallet balance */}
                <div className="flex items-center gap-1.5 mb-2.5 text-[#f59e0b]">
                  <Wallet size={15} />
                  <span className="text-base font-bold">{u.wallet_balance != null ? `${Number(u.wallet_balance).toFixed(2)} ${u.balance_currency || "USDT"}` : "—"}</span>
                  {u.cloudbet_connected && u.wallet_balance == null && (
                    u.cloudbet_verified === false
                      ? <span className="text-[11px] text-[#ef4444]">(key not authenticating)</span>
                      : <span className="text-[11px] text-[#64748b]">(syncing…)</span>
                  )}
                </div>

                {/* Connection status row */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2.5">
                  <Stat ok={u.cloudbet_verified === true ? true : (u.cloudbet_connected ? false : null)} label="Cloudbet" />
                  <Stat ok={u.telegram_connected} label="Telegram" />
                  <Stat ok={u.telegram_test_success} label="Test alert" />
                </div>

                {/* Trading status */}
                {!u.is_admin && (
                  <div data-testid={`trading-status-${u.token_short}`} className="rounded-lg border border-[#334155] bg-[#0f172a] p-2.5 mb-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold" style={{ color: u.auto_trading ? "#10b981" : "#ef4444" }}>
                        <Zap size={13} /> Auto-trading {u.auto_trading ? "ON" : "OFF"}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[11px] font-semibold" style={{ backgroundColor: u.trading_ready ? "#10b98130" : "#ef444430", color: u.trading_ready ? "#10b981" : "#fca5a5" }}>
                        {u.trading_ready ? "Ready to trade" : "Not trading"}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#94a3b8] mb-1">
                      Strategies: {u.enabled_strategy_count > 0 ? u.enabled_strategies.join(", ") : <span className="text-[#ef4444]">none enabled</span>}
                    </div>
                    {Array.isArray(u.trading_blockers) && u.trading_blockers.length > 0 && (
                      <ul className="space-y-0.5 mt-1">
                        {u.trading_blockers.map((b, i) => (
                          <li key={i} className="flex items-start gap-1 text-[11px] text-[#fca5a5]"><span>•</span><span>{b}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Profile details */}
                <div className="space-y-1 border-t border-[#334155] pt-2 mb-2">
                  <Row k="Email" v={u.email} />
                  <Row k="Invite code" v={u.code_used} />
                  <Row k="Device" v={u.device_name} />
                  <Row k="Telegram" v={u.telegram} />
                  <Row k="Discord" v={u.discord} />
                  <Row k="X" v={u.x_handle} />
                  <Row k="Last seen" v={u.last_seen ? new Date(u.last_seen).toLocaleString() : ""} />
                </div>

                {!u.is_admin && (
                  <div className="flex items-center gap-2 justify-end">
                    {u.active && <button onClick={() => revokeUser(u.token)} data-testid={`revoke-user-${u.token_short}`} className="flex items-center gap-1 bg-[#f59e0b]/20 text-[#f59e0b] text-[12px] font-semibold px-2.5 py-1.5 rounded-md"><UserMinus size={14} /> Revoke</button>}
                    <button onClick={() => askDeleteUser(u.token, fullName)} data-testid={`delete-user-${u.token_short}`} className="flex items-center gap-1 bg-[#ef4444]/20 text-[#ef4444] text-[12px] font-semibold px-2.5 py-1.5 rounded-md"><Trash2 size={14} /> Delete</button>
                  </div>
                )}
              </div>
            );})}
            </div>
            </>
          )
        )}
        {section === "engine" && (
          <div data-testid="engine-diagnostics">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-[#94a3b8] font-semibold">Live Trading Engine Diagnostics</p>
              <button onClick={loadDiag} data-testid="reload-diag-btn" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#10b981]"><RefreshCw size={15} /> Reload</button>
            </div>
            {diagLoading && <p className="text-[#64748b]">Loading…</p>}
            {!diagLoading && !diag && <p className="text-[#64748b]">No data. Tap Reload.</p>}
            {diag && (
              <>
                {/* Engine + monitor summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
                  <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
                    <p className="text-[11px] text-[#64748b] mb-0.5">Trading engine</p>
                    <p className="text-sm font-bold" style={{ color: diag.engine_enabled ? "#10b981" : "#ef4444" }}>{diag.engine_enabled ? "ENABLED" : "DISABLED"}</p>
                  </div>
                  <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
                    <p className="text-[11px] text-[#64748b] mb-0.5">Monitor loop</p>
                    <p className="text-sm font-bold" style={{ color: diag.monitor?.loop_alive ? "#10b981" : "#ef4444" }}>{diag.monitor?.loop_alive ? "ALIVE" : "STALLED"}</p>
                    <p className="text-[10px] text-[#64748b]">{diag.monitor?.last_cycle_age_seconds != null ? `${diag.monitor.last_cycle_age_seconds}s ago` : "never"} · {diag.monitor?.cycles || 0} cycles</p>
                  </div>
                  <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
                    <p className="text-[11px] text-[#64748b] mb-0.5">Active traders</p>
                    <p className="text-sm font-bold text-[#f1f5f9]">{diag.user_manager?.active_users || 0} / {diag.user_manager?.total_users || 0}</p>
                    <p className="text-[10px] text-[#64748b]">{diag.monitor?.live_matches || 0} live matches</p>
                  </div>
                  <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
                    <p className="text-[11px] text-[#64748b] mb-0.5">Last monitor error</p>
                    <p className="text-[11px] font-semibold" style={{ color: diag.monitor?.last_error ? "#fca5a5" : "#10b981" }}>{diag.monitor?.last_error || "none"}</p>
                  </div>
                </div>
                {!diag.engine_enabled && (
                  <div className="flex items-start gap-2 bg-[#ef4444]/15 border border-[#ef4444]/40 rounded-xl p-3 mb-3 text-[#fca5a5]">
                    <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                    <span className="text-[13px] font-semibold">ENABLE_TRADING_ENGINE is OFF on this server — no auto-trades will run for anyone. Redeploy with ENABLE_TRADING_ENGINE=true.</span>
                  </div>
                )}
                {/* Per-user engine state */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                  {(diag.users || []).map((u, i) => (
                    <div key={i} data-testid={`engine-user-${u.code_used || i}`} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3.5">
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="text-sm font-bold text-[#f1f5f9]">{u.name || u.user_id_short}</span>
                        <span className="px-2 py-0.5 rounded text-[11px] font-semibold" style={{ backgroundColor: u.is_active_for_trading ? "#10b98130" : "#ef444430", color: u.is_active_for_trading ? "#10b981" : "#fca5a5" }}>{u.is_active_for_trading ? "TRADING" : "NOT TRADING"}</span>
                      </div>
                      {u.code_used && <p className="text-[11px] text-[#64748b] mb-1.5">Code: <span className="text-[#cbd5e1] font-semibold tracking-wide">{u.code_used}</span></p>}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mb-1.5">
                        <span style={{ color: u.auto_trading ? "#10b981" : "#ef4444" }}>Auto-trade: {u.auto_trading ? "ON" : "OFF"}</span>
                        <span style={{ color: u.engine_wallet_connected ? "#10b981" : "#ef4444" }}>Engine wallet: {u.engine_wallet_connected ? "connected" : (u.engine_wallet_present ? "FAILED" : "none")}</span>
                        <span className="text-[#94a3b8]">Balance: {u.cached_balance != null ? u.cached_balance : "—"}</span>
                        <span className="text-[#94a3b8]">Trades 24h: {u.auto_trades_last_24h != null ? u.auto_trades_last_24h : "—"}</span>
                      </div>
                      <p className="text-[11px] text-[#94a3b8] mb-1">Strategies: {u.enabled_strategies?.length ? u.enabled_strategies.join(", ") : <span className="text-[#ef4444]">none</span>}</p>
                      {!u.is_admin && u.user_id && (
                        <button onClick={() => askDeleteUser(u.user_id, u.name)} data-testid={`engine-delete-user-${u.user_id_short}`} className="flex items-center gap-1 bg-[#ef4444]/20 text-[#ef4444] text-[11px] font-semibold px-2 py-1 rounded-md mb-1.5 hover:bg-[#ef4444]/30 transition-colors"><Trash2 size={13} /> Delete profile</button>
                      )}
                      {!u.is_active_for_trading && Array.isArray(u.not_active_reasons) && u.not_active_reasons.length > 0 && (
                        <ul className="space-y-0.5 mt-1">
                          {u.not_active_reasons.map((r, j) => (<li key={j} className="flex items-start gap-1 text-[11px] text-[#fca5a5]"><span>•</span><span>{r}</span></li>))}
                        </ul>
                      )}
                      {/* Live trade feed */}
                      <div className="mt-2.5 border-t border-[#334155] pt-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#64748b] mb-1">Recent signals</p>
                        {Array.isArray(u.recent_feed) && u.recent_feed.length > 0 ? (
                          <ul className="space-y-1 max-h-44 overflow-y-auto" data-testid={`feed-${u.code_used || i}`}>
                            {u.recent_feed.map((f, k) => {
                              const c = f.status === "PLACED" ? "#10b981" : f.status === "PENDING" ? "#f59e0b" : f.status === "FAILED" ? "#ef4444" : "#94a3b8";
                              return (
                                <li key={k} className="text-[11px] leading-tight">
                                  <div className="flex items-center gap-1.5">
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0" style={{ backgroundColor: c + "22", color: c }}>{f.status}</span>
                                    <span className="text-[#e2e8f0] font-medium truncate">{f.player}</span>
                                    <span className="text-[#64748b] shrink-0">{f.at ? new Date(f.at + "Z").toLocaleTimeString() : ""}</span>
                                  </div>
                                  <p className="text-[#94a3b8] mt-0.5">{f.strategy ? `${f.strategy} — ` : ""}{f.reason}</p>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-[11px] text-[#64748b]">No signals yet.</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirm}
        loading={confirmLoading}
        title={confirmAction?.title || "Confirm"}
        message={confirmAction?.message || ""}
        confirmText="Delete"
        danger
      />
    </div>
  );
}
